/*
 * File: RelatedModule.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on July 21, 2025 at 1:02 PM
 * Copyright © 2025 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: July 21, 2025 at 1:33 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	memo, useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';

// Utilities
import { isOverflowing } from '../../../utilities/rendering';

// Components
import { Typography } from '../../Typography';
import { LocaleLink } from '../../LocaleLink';
import { ObfuscatedText } from '../../ObfuscatedText';

// Sources
import { SOURCES } from '../../../../constants/base.constants';

// Styles
import * as S from './RelatedModule.styles';


/**
 * Component
 */

export const RelatedModule = memo(({
	module, source, sourceId, onRelatedClick
}) => {

	// Create state handlers
	const [charCount, setCharCount] = useState(75);

	// Create module element reference
	const modElementRef = useRef();

	// Get locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get full text of question
	const fullText = useMemo(() => module?.question_locales?.[locale.localeBaseId], [module, locale]);

	// Handle on related click
	const onRelated = async () => {
		if (onRelatedClick) onRelatedClick();
	};

	// Handle truncate middle of content
	const truncateMiddle = (str, startCharCount, endCharCount) => {

		// No truncation needed if the string is short enough
		if (str.length <= startCharCount + endCharCount) {
			return str;
		}

		// Get start content
		let start = str.substring(0, startCharCount);
		start = start.substring(0, start.lastIndexOf(' ')).trim();

		// Get end content
		let end = str.substring(str.length - endCharCount);
		end = end.substring(end.indexOf(' '), end.length).trim();

		// Return truncated content
		return `${start}...${end}`;
	};

	// Handle module element changes
	useEffect(() => {
		if (modElementRef?.current && isOverflowing(modElementRef?.current)) {
			setCharCount(Math.max(charCount - 10, 0));
		}
	}, [modElementRef, charCount]);

	// Render component
	return (
		<LocaleLink to={`/answer/${module.slug}`} onClick={onRelated} title={fullText}>
			<S.RelatedCard>
				<FontAwesomeIcon icon={['fas', 'arrow-up-right']} />
				<S.RelatedClampContent clamp={4}>
					<Typography
						ref={modElementRef}
						weight="regular"
						tag="p"
						variation="2"
					>
						<ObfuscatedText>
							{truncateMiddle(fullText, charCount, charCount)}
						</ObfuscatedText>
					</Typography>
				</S.RelatedClampContent>
			</S.RelatedCard>
		</LocaleLink>
	);
});


/**
 * Configuration
 */

RelatedModule.displayName = 'RelatedModule';
RelatedModule.propTypes = {
	module: PropTypes.shape(),
	source: PropTypes.oneOf(Object.values(SOURCES)),
	sourceId: PropTypes.string,
	onRelatedClick: PropTypes.func,
};
RelatedModule.defaultProps = {
	module: null,
	source: null,
	sourceId: null,
	onRelatedClick: null
};
